/**
 * ProvaLab.io cross-app product switcher.
 *
 * Drop this component once at the app root (App.tsx / _app.tsx) of any
 * ProvaLab frontend. It renders a fixed-position waffle icon in the top-right
 * corner; on click, it opens a dropdown listing every ProvaLab product with
 * the current app highlighted.
 *
 * Design goals:
 *   - Zero UI-framework dependency (works in MUI, Tailwind, plain-CSS apps)
 *   - Self-contained styles (all inline; no CSS import required)
 *   - Detects the current app from window.location.hostname — no props needed
 *   - Shows the Admin link only to platform staff: it asks the admin BFF
 *     (admin.provalab.io/api/v1/session) and needs is_employee === true.
 *     admin.provalab.io is IP-allowlisted, so from outside the request fails
 *     and the link stays hidden. Any error also hides it (fail closed).
 *
 * Deployment:
 *   Copy this file to `src/components/AppSwitcher.tsx` in every frontend
 *   (tms-frontend, qa-agent-frontend, qainfra-frontend, admin-portal-frontend)
 *   and add `<AppSwitcher />` inside the app root. That's it.
 */

import { useState, useEffect, useRef } from "react";

interface AppLink {
  key: string;
  name: string;
  description: string;
  href: string;
  hostname: string;
  icon: string; // emoji or single-char label
  color: string;
  adminOnly?: boolean;
}

// Order matches nav priority. Current app is highlighted at render.
const APPS: AppLink[] = [
  {
    key: "tms",
    name: "Test Management",
    description: "Plans, cases, runs, coverage",
    href: "https://tms.provalab.io/",
    hostname: "tms.provalab.io",
    icon: "📋",
    color: "#2563eb",
  },
  {
    key: "qa-agent",
    name: "QA AI Agent",
    description: "Chat with your AI QA teammate",
    href: "https://qa.provalab.io/",
    hostname: "qa.provalab.io",
    icon: "🤖",
    color: "#0891b2",
  },
  {
    key: "qa-infra",
    name: "QA Infrastructure",
    description: "Real devices + browsers on demand",
    href: "https://infra.provalab.io/",
    hostname: "infra.provalab.io",
    icon: "⚙️",
    color: "#7c3aed",
  },
  {
    key: "security",
    name: "Security",
    description: "Scan apps and APIs, triage findings",
    href: "https://security.provalab.io/",
    hostname: "security.provalab.io",
    icon: "🛡️",
    color: "#b91c1c",
  },
  {
    key: "load",
    name: "Load Testing",
    description: "Load test APIs and web apps at scale",
    href: "https://load.provalab.io/",
    hostname: "load.provalab.io",
    icon: "⚡",
    color: "#0f766e",
  },
  {
    key: "reviews",
    name: "PR Reviews",
    description: "Test impact and AI review on pull requests",
    href: "https://reviews.provalab.io/",
    hostname: "reviews.provalab.io",
    icon: "🔀",
    color: "#ea580c",
  },
  {
    key: "auth",
    name: "Account & Auth",
    description: "Profile, orgs, sessions, API keys",
    href: "https://auth.provalab.io/dashboard",
    hostname: "auth.provalab.io",
    icon: "🔐",
    color: "#059669",
  },
  {
    key: "admin",
    name: "Admin Portal",
    description: "Platform-wide administration",
    href: "https://admin.provalab.io/",
    hostname: "admin.provalab.io",
    icon: "🛡️",
    color: "#dc2626",
    adminOnly: true,
  },
  {
    key: "plugins",
    name: "Plugins",
    description: "SDKs & test-framework reporters",
    href: "https://plugins.provalab.io/",
    hostname: "plugins.provalab.io",
    icon: "🧩",
    color: "#9333ea",
  },
  {
    key: "docs",
    name: "Docs",
    description: "Guides, API reference, tutorials",
    href: "https://docs.provalab.io/",
    hostname: "docs.provalab.io",
    icon: "📚",
    color: "#0284c7",
  },
  {
    key: "www",
    name: "provalab.io",
    description: "Marketing site + pricing",
    href: "https://provalab.io/",
    hostname: "provalab.io",
    icon: "🌐",
    color: "#475569",
  },
];

const ADMIN_SESSION_URL = "https://admin.provalab.io/api/v1/session";

export function AppSwitcher() {
  const [open, setOpen] = useState(false);
  const [currentHost, setCurrentHost] = useState<string>("");
  const [isEmployee, setIsEmployee] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setCurrentHost(window.location.hostname);
    // qa_session is HttpOnly, so ask the admin BFF instead of reading cookies.
    if (window.location.hostname.endsWith("provalab.io")) {
      fetch(ADMIN_SESSION_URL, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => setIsEmployee(body?.is_employee === true))
        .catch(() => setIsEmployee(false));
    }

    // Close on outside click
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Hide the switcher on hosts we don't manage (safety guard for local dev)
  const isProvalabHost = currentHost.endsWith("provalab.io");
  if (!isProvalabHost) return null;

  const visibleApps = APPS.filter((a) => !a.adminOnly || isEmployee);

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: 12,
        right: 12,
        zIndex: 2147483000, // above nearly everything, below dev-tools overlays
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        aria-label="Switch ProvaLab app"
        aria-expanded={open}
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(8px)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: open
            ? "0 8px 24px rgba(0,0,0,0.15)"
            : "0 2px 8px rgba(0,0,0,0.06)",
          transition: "box-shadow 150ms ease",
        }}
      >
        <WaffleIcon color="#475569" />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            width: 320,
            background: "white",
            borderRadius: 14,
            boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 16px 8px",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            ProvaLab.io products
          </div>
          <div style={{ padding: "4px 8px 8px" }}>
            {visibleApps.map((app) => {
              const isCurrent = currentHost === app.hostname;
              return (
                <a
                  key={app.key}
                  href={app.href}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    textDecoration: "none",
                    color: "#0f172a",
                    background: isCurrent ? "#f1f5f9" : "transparent",
                    transition: "background 120ms ease",
                    cursor: isCurrent ? "default" : "pointer",
                    pointerEvents: isCurrent ? "none" : "auto",
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: `${app.color}15`,
                      color: app.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {app.icon}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>
                      {app.name}
                      {isCurrent && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: 6,
                            background: "#e2e8f0",
                            color: "#475569",
                            verticalAlign: "middle",
                          }}
                        >
                          current
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, lineHeight: 1.3 }}>
                      {app.description}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
          <div
            style={{
              padding: "10px 16px",
              borderTop: "1px solid #f1f5f9",
              fontSize: 11,
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            Signed in on <b style={{ color: "#475569" }}>*.provalab.io</b> — switch freely
          </div>
        </div>
      )}
    </div>
  );
}

function WaffleIcon({ color = "currentColor" }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill={color} xmlns="http://www.w3.org/2000/svg">
      {[
        [2, 2], [9, 2], [16, 2],
        [2, 9], [9, 9], [16, 9],
        [2, 16], [9, 16], [16, 16],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="1.6" />
      ))}
    </svg>
  );
}

export default AppSwitcher;
